import { transaction } from '@src/js/utils';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IThemeColorManager } from '@src/slide/application/service/theme-color-manager/index.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { GradientType, IGradFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ICTColorDmlA, ILayoutDataModel, STSchemeColorVal } from '@tencent/slide-sdk-model';
import { GradColor, GradColorResult, SolidColorResult } from './interface';

export const GradPreset: GradColor[] = [
    ['#E5EFFF', '#99BEFF'],
    ['#FFF9E3', '#C7ECFF'],
    ['#D9EAD3', '#678F00'],
    ['#8C7BE8', '#FEE4FF'],
    ['#EE93F6', '#FFD966'],
    ['#FFB700', '#E75200'],
    ['#C4C4C4', '#9D9D9D'],
];

export const getGradFillStyle = (from: string, to: string) => `linear-gradient(225deg,${from} 0%,${to} 100%)`;

export const createGradFill = (from: string, to: string, fromAlpha?: number): IGradFill => ({
    gsList: [
        {
            pos: 0,
            color: new TDColor({ rgbaStr: from, alpha: fromAlpha }),
        },
        {
            pos: 100,
            color: new TDColor({ rgbaStr: to }),
        },
    ],
    pathGradient: { path: GradientType.Linear },
    rotateWithShape: 1,
    lin: { ang: 135, scaled: 1 },
});

interface ColorOptions {
    themeKey: number;
    color: string;
}

export class BackgroundFillTask {
    public constructor(
        private uiDataService: IUiDataService,
        private selectionService: ISelectionService,
        private themeColorManager?: IThemeColorManager,
    ) { }

    public updateBackgroundFill = (range: 'current' | 'all', color: SolidColorResult | GradColorResult) => {
        const updateSlide = (slide: ISlideModel) => {
            if ('gradColor' in color) {
                // 渐变填充
                slide.getBackgroundFill().updateCurrentFillForCurrentModel({
                    gradFill: createGradFill(...color.gradColor, 1 - (color.transparency / 100)),
                    shadeToTitle: 0,
                });
            } else {
                // 纯色填充
                const clr = color.enableCustom ? color.customColor : new TDColor({ themeKey: color.themeKey });
                clr.originAlpha = 1 - (color.transparency / 100);
                slide.getBackgroundFill().updateCurrentFillForCurrentModel({
                    solidFill: { color: clr },
                });
            }
            // 统一开启 《隐藏背景图形》，否则背景色可能被母版上的图片遮挡，最终用户看不到任何变化
            (slide.sdkData as ILayoutDataModel)?.api.updateShowMasterSp(false);
        };

        transaction(() => {
            if (range === 'all') {
                this.uiDataService.presentation.children.forEach(updateSlide);
            } else {
                const { currentSlide } = this.selectionService;
                currentSlide && updateSlide(currentSlide);
            }
        });
    };

    public getColorOptions = (): ColorOptions[] => {
        const currentThemeModel = this.themeColorManager?.currentThemeModel;
        if (!currentThemeModel) return [];

        const isDark = this.selectionService.currentSlide
            ?.getRelationMasterModel().clrMap.tx1 === STSchemeColorVal.lt1;
        const clrScheme = currentThemeModel.sdkData?.props.themeElements?.clrScheme;
        if (!clrScheme) return [];

        type STSchemeColorKey = keyof typeof STSchemeColorVal;
        const preset: STSchemeColorKey[] = isDark
            ? ['dk1', 'dk2', 'accent3', 'accent4', 'accent5', 'accent6']
            : ['lt1', 'lt2', 'accent3', 'accent4', 'accent5', 'accent6'];

        return preset.map(value => ({
            themeKey: STSchemeColorVal[value] as STSchemeColorVal,
            color: `#${(clrScheme[value] as ICTColorDmlA).srgbClr?.val}`,
        }));
    };
}
